import { logClientError } from '@utils/server-log';
import { Injectable } from '@angular/core';
import { ORGANIZATION_ID } from '@models/organization';
import { SocialAuthProvider } from '@models/user';
import * as AuthProto from '@proto/auth/auth';
import * as PaginationCommonProto from '@proto/common/pagination';
import * as TimestampProto from '@proto/google/protobuf/timestamp';
import { SharedService } from '@services/shared/shared.service';
import { AuthorizationInterceptor } from 'src/app/interceptors/interceptor';
import { environment } from 'src/environments/environment';
import { endpointBaseGRPC } from 'src/main';

export interface UserListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  name?: string;
  email?: string;
  status?: number;
}

export interface AddressParams {
  zipCode?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  country?: string;
}

export interface UpdateUserParams {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  document?: string;
  phone?: string;
  address?: AddressParams;
  /** Razão social — apenas para pessoa jurídica (CNPJ). */
  companyName?: string;
  /** Data de nascimento — apenas para pessoa física (CPF). */
  birthDate?: TimestampProto.google.protobuf.Timestamp;
}

export interface LoginParams {
  username: string;
  password: string;
}

export interface UserListParams {
  pagination?: PaginationCommonProto.common.v1.PaginationRequest;
  filter?: AuthProto.auth.v1.ListUsersFilter;
}

export interface PasswordlessParams {
  identifier: string;
}

export interface RefreshTokenParams {
  refreshToken: string;
}

export interface ForgotPasswordParams {
  email?: string;
  phone?: string;
}

export interface ResetPasswordParams {
  email?: string;
  phone?: string;
  code: string;
  newPassword: string;
}

export interface MFAValidationParams {
  mfaSessionId: string;
  mfaCode: string;
}

@Injectable({
  providedIn: 'root',
})
export class GrpcAuthService {
  private clientAuth = new AuthProto.auth.v1.AuthServiceClient(
    endpointBaseGRPC + '/auth',
    undefined,
    {
      unaryInterceptors: [new AuthorizationInterceptor()],
    }
  );

  constructor(private sharedService: SharedService) {}

  // Auth Service Methods
  login(params: LoginParams) {
    const passwordAuth = new AuthProto.auth.v1.PasswordAuth({
      username: params.username,
      password: params.password,
    });

    const request = new AuthProto.auth.v1.LoginRequest({
      passwordAuth: passwordAuth,
    });

    return this.clientAuth.Login(request, {});
  }

  loginPasswordless(params: PasswordlessParams) {
    const passwordlessAuth = new AuthProto.auth.v1.PasswordlessAuth({
      identifier: params.identifier,
    });

    const request = new AuthProto.auth.v1.LoginRequest({
      passwordlessAuth: passwordlessAuth,
    });

    return this.clientAuth.Login(request, {});
  }

  refreshToken(params: RefreshTokenParams) {
    const request = new AuthProto.auth.v1.RefreshTokenRequest({
      refreshToken: params.refreshToken,
    });

    return this.clientAuth.RefreshToken(request, {});
  }

  forgotPassword(params: ForgotPasswordParams) {
    const requestObj: any = {};
    if (params.email) {
      requestObj.email = params.email;
    } else if (params.phone) {
      requestObj.phone = params.phone;
    }

    const request = new AuthProto.auth.v1.ForgotMyPasswordRequest(requestObj);
    return this.clientAuth.ForgotMyPassword(request, {});
  }

  getUserMenu() {
    const request = new AuthProto.auth.v1.GetUserMenuRequest({});
    return this.clientAuth.GetUserMenu(request, {});
  }

  getUserPermissions() {
    const request = new AuthProto.auth.v1.GetUserPermissionsRequest({});
    return this.clientAuth.GetUserPermissions(request, {});
  }

  /**
   * Busca todos os roles/cargos do sistema com suas permissões
   */
  getRoles(): Promise<AuthProto.auth.v1.GetRolesResponse> {
    const request = new AuthProto.auth.v1.GetRolesRequest({});
    return this.clientAuth.GetRoles(request, {});
  }

  /**
   * Atualiza os roles/cargos com suas permissões de features
   */
  updateRoles(roles: AuthProto.auth.v1.Role[]): Promise<AuthProto.auth.v1.UpdateRolesResponse> {
    const request = new AuthProto.auth.v1.UpdateRolesRequest({ roles });
    return this.clientAuth.UpdateRoles(request, {});
  }

  resetPassword(params: ResetPasswordParams) {
    const requestObj: any = {
      code: params.code,
      newPassword: params.newPassword,
    };

    if (params.email) {
      requestObj.email = params.email;
    } else if (params.phone) {
      requestObj.phone = params.phone;
    }

    const request = new AuthProto.auth.v1.ResetMyPasswordRequest(requestObj);
    return this.clientAuth.ResetMyPassword(request, {});
  }

  logout(refreshToken: string) {
    const request = new AuthProto.auth.v1.LogoutRequest({
      refreshToken: refreshToken,
    });

    return this.clientAuth.Logout(request, {});
  }

  listUsers(params: UserListParams) {
    const request = new AuthProto.auth.v1.ListUsersRequest({
      pagination: params.pagination,
      filter: params.filter,
    });
    return this.clientAuth.ListUsers(request, {});
  }

  validateMFA(params: MFAValidationParams) {
    const request = new AuthProto.auth.v1.ValidateMFARequest({
      mfaSessionId: params.mfaSessionId,
      code: params.mfaCode,
    });

    return this.clientAuth.ValidateMFA(request, {});
  }

  getUserById(document: string) {
    const request = new AuthProto.auth.v1.GetAuthUserRequest({
      userId: document,
    });
    return this.clientAuth.GetAuthUser(request, {});
  }

  updateUser(params: UpdateUserParams) {
    const requestData: any = {
      userId: params.userId,
      firstName: params.firstName,
      lastName: params.lastName,
      email: params.email,
      username: params.username,
      document: params.document,
      phone: params.phone,
    };

    if (params.address) {
      requestData.address = new AuthProto.auth.v1.Address({
        zipCode: params.address.zipCode,
        street: params.address.street,
        number: params.address.number,
        complement: params.address.complement,
        neighborhood: params.address.neighborhood,
        city: params.address.city,
        state: params.address.state,
        country: params.address.country || 'BR',
      });
    }

    if (params.companyName !== undefined) {
      requestData.companyName = params.companyName;
    }

    if (params.birthDate) {
      requestData.birthDate = params.birthDate;
    }

    const request = new AuthProto.auth.v1.UpdateAuthUserRequest(requestData);
    return this.clientAuth.UpdateAuthUser(request, {});
  }

  deleteUser(userId: string): Promise<AuthProto.auth.v1.DeleteUserAdminResponse> {
    const request = new AuthProto.auth.v1.DeleteUserAdminRequest({
      userId: userId,
    });
    return this.clientAuth.DeleteUserAdmin(request, {});
  }

  // OAuth Methods for SVM Provider
  loginWithProvider(provider: SocialAuthProvider): Promise<AuthProto.auth.v1.LoginOAuthResponse> {
    const request = new AuthProto.auth.v1.LoginOAuthRequest({
      organizationId: ORGANIZATION_ID,
      redirectUri: window.location.origin,
      scope: environment.grpc.scope,
      provider,
      codeChallengeMethod: AuthProto.auth.v1.CodeChallengeMethod.CODE_CHALLENGE_METHOD_S256,
    });
    return this.clientAuth.LoginOAuth(request, {});
  }

  validateCodeOAuth(
    code: string,
    state: string
  ): Promise<AuthProto.auth.v1.ValidateCodeOAuthResponse> {
    const request = new AuthProto.auth.v1.ValidateCodeOAuthRequest({
      code,
      state,
    });
    return this.clientAuth.ValidateCodeOAuth(request, {});
  }

  async getAndSetCurrentUser(accessToken?: string): Promise<void> {
    if (accessToken) {
      this.sharedService.accessToken = accessToken;
    }

    if (!this.sharedService.accessToken) {
      return;
    }

    try {
      // Call GetAuthUser without userId to get current authenticated user
      const request = new AuthProto.auth.v1.GetAuthUserRequest({});
      const response = await this.clientAuth.GetAuthUser(request, {});
      const res = response.toObject();

      const user = {
        ...this.sharedService.user,
        ...res,
        name: `${res.firstName} ${res.lastName}`,
        preferred_username: `${res.firstName} ${res.lastName}`,
      };

      this.sharedService.user = user;
      this.sharedService.user$.next(user);
    } catch (error) {
      logClientError('Error fetching user data:', error);
      // Re-throw error so AuthGuard can handle it properly
      throw error;
    }
  }
}
